import { AuthService } from './authService';
import {
    CountingListPagedParams,
    CountingListPagedResponse,
    CreateCountingListRequest,
    CreateCountingListResponse,
    ItemBinCountingRow,
    CountingListOverviewItem,
    UpdateCountingListRequest,
    CountingListLine,
    CountingListLineRequest,
    AddLinesToCountingListResponse,
    ExportCountingListResponse,
    CountingListLinesPagedResponse
} from '../types/countingListCreation';
import { extractApiError } from '../utils/apiError';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

class CountingListCreationService {

    /**
     * Get Items joined with ItemBins (one row per item+bin) with server-side paging and filtering.
     */
    async getItemBinsPaged(params: CountingListPagedParams = {}): Promise<CountingListPagedResponse> {
        const queryParams = new URLSearchParams();

        // Pagination
        if (params.pageNumber !== undefined) {
            queryParams.append('pageNumber', params.pageNumber.toString());
        }
        if (params.pageSize !== undefined) {
            queryParams.append('pageSize', params.pageSize.toString());
        }

        // Sorting
        if (params.sortColumn) {
            queryParams.append('sortColumn', params.sortColumn);
        }
        if (params.sortDirection) {
            queryParams.append('sortDirection', params.sortDirection);
        }

        // Boolean filter
        if (params.includeZeroStock) {
            queryParams.append('includeZeroStock', 'true');
        }

        // Text/numeric filters
        const filterFields: (keyof CountingListPagedParams)[] = [
            'itemNo', 'description1', 'itemBarCode', 'supplierItemNo',
            'itemGrp1', 'itemGrp2', 'itemGrp3',
            'warehouseID', 'binCode', 'itemUnitCode', 'stdLocationName',
            'binQuantity', 'lastCountedDate', 'lastMovementDate'
        ];

        for (const field of filterFields) {
            const value = params[field];
            if (value !== undefined && value !== null && value !== '') {
                queryParams.append(field, String(value));
            }
        }

        const queryString = queryParams.toString();
        const url = `${API_BASE_URL}/counting-list-creation/items-bins/paged${queryString ? `?${queryString}` : ''}`;

        const response = await AuthService.authenticatedGet(url);

        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(extractApiError(errorData, response, 'Feil ved henting av vare-/lokasjonsliste'));
        }

        const result = await response.json();

        // Add rowKey and id to each row (computed on client)
        const rows: ItemBinCountingRow[] = (result.data || []).map((row: any) => {
            const rowKey = `${row.itemNo}|${row.warehouseID}|${row.binCode}`;
            return {
                ...row,
                rowKey,
                id: rowKey
            };
        });

        return {
            ...result,
            data: rows
        };
    }

    /**
     * Create a new counting list (StcTransOrder + StcTrans lines).
     */
    async createCountingList(request: CreateCountingListRequest): Promise<CreateCountingListResponse> {
        const response = await AuthService.authenticatedPost(
            `${API_BASE_URL}/counting-list-creation/create`,
            request
        );

        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(extractApiError(errorData, response, 'Feil ved opprettelse av telleliste'));
        }

        return await response.json();
    }

    /**
     * Get all counting lists with aggregated progress data.
     * Optionally filter by warehouse and/or operatorId.
     */
    async getCountingListOverview(warehouse?: string, operatorId?: string): Promise<CountingListOverviewItem[]> {
        const queryParams = new URLSearchParams();
        if (warehouse) queryParams.append('warehouse', warehouse);
        if (operatorId) queryParams.append('operatorId', operatorId);
        const qs = queryParams.toString();
        const url = `${API_BASE_URL}/counting-list-creation/overview${qs ? `?${qs}` : ''}`;

        const response = await AuthService.authenticatedGet(url);
        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(extractApiError(errorData, response, 'Feil ved henting av tellelisteoversikt'));
        }
        return await response.json();
    }

    /**
     * Get all lines (StcTrans) for a specific counting list.
     */
    async getCountingListLines(batchId: string): Promise<CountingListLine[]> {
        const url = `${API_BASE_URL}/counting-list-creation/${encodeURIComponent(batchId)}/lines`;

        const response = await AuthService.authenticatedGet(url);
        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(extractApiError(errorData, response, 'Feil ved henting av tellelistelinjer'));
        }
        return await response.json();
    }

    /**
     * Get lines for a counting list with pagination.
     */
    async getCountingListLinesPaged(batchId: string, pageNumber = 1, pageSize = 50): Promise<CountingListLinesPagedResponse> {
        const params = new URLSearchParams({
            pageNumber: pageNumber.toString(),
            pageSize: pageSize.toString()
        });
        const url = `${API_BASE_URL}/counting-list-creation/${encodeURIComponent(batchId)}/lines/paged?${params}`;

        const response = await AuthService.authenticatedGet(url);
        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(extractApiError(errorData, response, 'Feil ved henting av tellelistelinjer'));
        }
        return await response.json();
    }

    /**
     * Update corrected quantity for a counting list line.
     */
    async updateLineCorrection(batchId: string, lineId: number, quantityCorrected: number, updatedBy?: string): Promise<boolean> {
        const url = `${API_BASE_URL}/counting-list-creation/${encodeURIComponent(batchId)}/lines/${lineId}/correction`;

        const response = await AuthService.authenticatedPatch(url, { quantityCorrected, updatedBy });
        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(extractApiError(errorData, response, 'Feil ved oppdatering av korreksjon'));
        }
        const result = await response.json();
        return result.success;
    }

    /**
     * Update counting list header (description, operator assignment).
     */
    async updateCountingList(batchId: string, request: UpdateCountingListRequest): Promise<boolean> {
        const url = `${API_BASE_URL}/counting-list-creation/${encodeURIComponent(batchId)}`;

        const response = await AuthService.authenticatedPut(url, request);
        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(extractApiError(errorData, response, 'Feil ved oppdatering av telleliste'));
        }
        return true;
    }

    /**
     * Delete an entire counting list (header + all lines).
     */
    async deleteCountingList(batchId: string): Promise<boolean> {
        const url = `${API_BASE_URL}/counting-list-creation/${encodeURIComponent(batchId)}`;

        const response = await AuthService.authenticatedDelete(url);
        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(extractApiError(errorData, response, 'Feil ved sletting av telleliste'));
        }
        return true;
    }

    /**
     * Delete a single line from a counting list.
     */
    async deleteCountingListLine(batchId: string, lineId: number): Promise<boolean> {
        const url = `${API_BASE_URL}/counting-list-creation/${encodeURIComponent(batchId)}/lines/${lineId}`;

        const response = await AuthService.authenticatedDelete(url);
        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(extractApiError(errorData, response, 'Feil ved sletting av tellelistelinje'));
        }
        return true;
    }

    /**
     * Add new lines to an existing counting list.
     */
    async addLinesToCountingList(batchId: string, lines: CountingListLineRequest[]): Promise<AddLinesToCountingListResponse> {
        const url = `${API_BASE_URL}/counting-list-creation/${encodeURIComponent(batchId)}/lines`;

        const response = await AuthService.authenticatedPost(url, lines);
        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(extractApiError(errorData, response, 'Feil ved tillegging av linjer'));
        }
        return await response.json();
    }

    /**
     * Reset a counted line so the operator can recount it.
     * Only allowed when the counting list is still active (OrderStatus < 50).
     */
    async resetLineCount(batchId: string, lineId: number): Promise<boolean> {
        const url = `${API_BASE_URL}/counting-list-creation/${encodeURIComponent(batchId)}/lines/${lineId}/reset`;

        const response = await AuthService.authenticatedPatch(url, {});
        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(extractApiError(errorData, response, 'Feil ved nullstilling av tellelinje'));
        }
        const result = await response.json();
        return result.success;
    }

    /**
     * Record an operator's counted quantity for a counting list line.
     */
    async recordLineCount(batchId: string, lineId: number, quantity: number): Promise<boolean> {
        const url = `${API_BASE_URL}/counting-list-creation/${encodeURIComponent(batchId)}/lines/${lineId}/count`;

        const response = await AuthService.authenticatedPatch(url, { quantity });
        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(extractApiError(errorData, response, 'Feil ved registrering av telling'));
        }
        const result = await response.json();
        return result.success;
    }

    /**
     * Complete a counting list (operator marks as done → OrderStatus 50).
     */
    async completeCountingList(batchId: string): Promise<{ success: boolean; errorMessage?: string; totalLines: number; countedLines: number; uncountedLines: number }> {
        const url = `${API_BASE_URL}/counting-list-creation/${encodeURIComponent(batchId)}/complete`;

        const response = await AuthService.authenticatedPost(url, {});
        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(extractApiError(errorData, response, 'Feil ved fullføring av telleliste'));
        }
        return await response.json();
    }

    /**
     * Export a counting list by BatchID.
     * Uses the inventory-counting-dashboard export endpoint with batch-based export.
     */
    async exportCountingList(batchId: string, warehouseId: string, exportedBy?: string): Promise<ExportCountingListResponse> {
        const url = `${API_BASE_URL}/inventory-counting-dashboard/export`;

        const response = await AuthService.authenticatedPost(url, {
            warehouseID: warehouseId,
            batchID: batchId,
            itemNumbers: [],
            exportedBy
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(extractApiError(errorData, response, 'Feil ved eksport av telleliste'));
        }
        return await response.json();
    }
}

export const countingListCreationService = new CountingListCreationService();
